import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import { DashboardSyncSettings, WidgetConfig, WidgetDashboardConfig, WidgetSettings } from '../components/widgetDashboard/widgetDashboard.interface';
import { MultiPaneItem } from '../components/multiPane/multiPaneLayout.interface';
import { countVisiblePanes } from "../components/multiPane/multipPaneLayout.helper";
import { WidgetTypes } from "../components/widgetDashboard/widgetDashboard.enums";

export interface WidgetDashboardStateType {
    layouts: Record<string, WidgetDashboardConfig>
}

/** Layouts may be created incrementally (panes first, widgets later), so a missing entry is treated as an empty config. */
const getExistingLayout = (state: WidgetDashboardStateType, id: string): WidgetDashboardConfig =>
    state.layouts[id] || ({} as WidgetDashboardConfig)

const initialState: WidgetDashboardStateType = {
    layouts: {}
};

const widgetDashboardSlice = createSlice({
    name: 'widgetDashboard',
    initialState,
    reducers: {
        deleteLayouts(state: WidgetDashboardStateType) {
            return {
                ...state,
                layouts: {}
            }
        },
        deleteLayoutWithId(state: WidgetDashboardStateType, action: PayloadAction<{
            id: string
            }>) {
            const { id } = action.payload;
            const layouts = {...state.layouts};
            delete layouts[id]
            return {
                ...state,
                layouts
            }
        },
        deleteLayoutsWithIds(state: WidgetDashboardStateType, action: PayloadAction<{
            ids: string[]
            }>) {
            const { ids } = action.payload;
            const layouts = {...state.layouts};
            ids.forEach(id=>{
                delete layouts[id]
            })
            return {
                ...state,
                layouts
            }
        },
        updateLayout(state: WidgetDashboardStateType, action: PayloadAction<{
            id: string,
            layout: WidgetDashboardConfig
            }>) {
            const {id, layout} = action.payload;
            const stateLayout = {
                ...getExistingLayout(state, id),
                ...layout
            }
            if (stateLayout.panes) {
                stateLayout.singleMode = countVisiblePanes(stateLayout.panes) <= 1
            }
            return {
                ...state,
                layouts: {
                    ...state.layouts,
                    [id]: stateLayout
                }
            }
        },
        updateLayoutPanes(state: WidgetDashboardStateType, action: PayloadAction<{
            id: string,
            panes: MultiPaneItem
            }>) {
            const {id, panes} = action.payload;
            const stateLayout = {
                ...getExistingLayout(state, id),
                panes
            }
            stateLayout.singleMode = countVisiblePanes(stateLayout.panes) <= 1
            return {
                ...state,
                layouts: {
                    ...state.layouts,
                    [id]: {
                        ...stateLayout
                    }
                }
            }
        },
        updateWidgetType(state: WidgetDashboardStateType, action: PayloadAction<{
            id: string,
            slotId: number,
            type: WidgetTypes
        }>) {
            const {id, slotId, type} = action.payload;
            const layout = state.layouts[id];
            if (!layout?.widgets) return state;
            const existingWidget = layout.widgets[slotId];
            return {
                ...state,
                layouts: {
                    ...state.layouts,
                    [id]: {
                        ...layout,
                        widgets: {
                            ...layout.widgets,
                            [slotId]: {
                                ...existingWidget,
                                widgetType: type
                            }
                        }
                    }
                }
            }
        },
        replaceWidget(state: WidgetDashboardStateType, action: PayloadAction<{
            id: string,
            slotId: number,
            widgetType: WidgetTypes,
            settings: WidgetSettings<any>
        }>) {
            const { id, slotId, widgetType, settings } = action.payload;
            const layout = state.layouts[id];
            const existingWidget = layout?.widgets?.[slotId];
            if (!layout || !existingWidget) return state;

            layout.widgets[slotId] = {
                ...existingWidget,
                slotId,
                widgetType,
                settings
            };
            layout.activeWidget = slotId;
        },
        updateWidgets(state: WidgetDashboardStateType, action: PayloadAction<{
            id: string,
            widgets: WidgetDashboardConfig['widgets']
            }>) {
            const {id, widgets} = action.payload;
            return {
                ...state,
                layouts: {
                    ...state.layouts,
                    [id]: {
                        ...getExistingLayout(state, id),
                        widgets
                    }
                }
            }
        },
        addWidget(state: WidgetDashboardStateType, action: PayloadAction<{
            id: string,
            widget: WidgetConfig<any>
        }>) {
            const {id, widget} = action.payload;
            const widgets = {...(state.layouts[id]?.widgets || {})}
            widgets[widget.slotId] = widget
            const newState: WidgetDashboardStateType = {
                ...state,
                layouts: {
                    ...state.layouts,
                    [id]: {
                        ...state.layouts[id],
                        widgets,
                        singleMode: Object.keys(widgets).length === 1,
                        activeWidget: widget.slotId
                    }
                }
            }
            return newState;
        },
        deleteWidget(state: WidgetDashboardStateType, action: PayloadAction<{
            id: string,
            slotId: number
        }>) {
            const {id, slotId} = action.payload;
            const widgets = {...(state.layouts[id]?.widgets || {})}
            delete widgets[slotId];
            const newState = {
                ...state,
                layouts: {
                    ...state.layouts,
                    [id]: {
                        ...state.layouts[id],
                        widgets,
                        singleMode: Object.keys(widgets).length === 1
                    }
                }
            }

            return newState;
        },
        updateWidgetSettings(state, action: PayloadAction<{
            id: string,
            slotId: number,
            settings: Partial<WidgetSettings<any>>
        }>){
            const {id, slotId, settings} = action.payload;
            const widgets = {...(state.layouts[id]?.widgets || {})}
            const widget = widgets[slotId];
            if (!widget) return state;
            const newState = {
                ...state,
                layouts: {
                    ...state.layouts,
                    [id]: {
                        ...state.layouts[id],
                        widgets: {
                            ...widgets,
                            [slotId]: {
                                ...widget,
                                settings: {
                                    ...widget.settings,
                                    ...settings
                                }
                            }
                        }
                    }
                }
            }

            return newState;
        },
        setActiveWidget(state, action: PayloadAction<{
            id: string,
            slotId: number
        }>) {
            const {id, slotId} = action.payload;
            return {
                ...state,
                layouts: {
                    ...state.layouts,
                    [id]: {
                        ...getExistingLayout(state, id),
                        activeWidget: slotId
                    }
                }
            }
        },
        updateSyncSettings(state, action: PayloadAction<{
            id: string,
            syncSettings: DashboardSyncSettings
        }>) {
            const {id, syncSettings} = action.payload;

            return {
                ...state,
                layouts: {
                    ...state.layouts,
                    [id]: {
                        ...getExistingLayout(state, id),
                        syncSettings
                    }
                }
            }
        },
        updateUserSavedLayouts(state, action: PayloadAction<{replace: boolean, layouts:Record<string, WidgetDashboardConfig>}>) {

            const {replace, layouts} = action.payload;
            if (replace) {
                return {
                    ...state,
                    layouts
                };
            }

            let newLayouts = {...state.layouts};
            for (const key in layouts) {
                newLayouts[key] = {
                    ...(newLayouts[key]||{}),
                    ...layouts[key]
                }
            }
            return {
                ...state,
                layouts: {
                    ...newLayouts,
                }
            }
        }
    },
})


const { actions, reducer } = widgetDashboardSlice;

export const {
    updateLayout, updateLayoutPanes, updateWidgetType, replaceWidget, updateWidgets, setActiveWidget, updateUserSavedLayouts,
    deleteWidget, addWidget, updateWidgetSettings, updateSyncSettings, deleteLayouts, deleteLayoutWithId, deleteLayoutsWithIds
} = actions;

export default reducer;